import React, { useState, useRef, useEffect } from 'react-x';
import propTypes from 'prop-types-x';
import { useUpdateEffect } from '../hooks/useUpdateEffect.js';
import { CORE_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { fireGTMTrackingEvent } from '../../utilities/fireGTMTrackingEvent.js';
import { detectBrowser } from '../../utilities/detectBrowser.js';

export function VideoPlayer(props) {
  const {
    src,
    captions,
    subtitles,
    label,
    playIcon,
    playIconHover,
    poster,
    fullscreen,
    assetRootPath,
    onPlay,
    onEnd, 
    onPause,
    gtmComponentName,
    gtmComponentGroup,
    playTrackingOverride
  } = props;

  const [videoState, setVideoState] = useState(false);
  const videoRef = useRef();
  const isNotSafariBrowserRef = useRef(detectBrowser() !== 'Apple Safari');
  const block = 'c-video-player';
  const buttonLabel = videoState ? 'pause video' : 'play video';

  useUpdateEffect(() => {
    const videoEl = videoRef.current;

    if (videoState === 'play') {  
      videoEl.play();

      if (isNotSafariBrowserRef.current) {
        videoEl.muted = false;
      }
 
      if (fullscreen) {
        if (videoEl.requestFullscreen) {
            videoEl.requestFullscreen();
        } else if (videoEl.webkitRequestFullscreen) {
            videoEl.webkitRequestFullscreen();
        } else if (videoEl.mozRequestFullScreen) {
            videoEl.mozRequestFullScreen();
        } else if (videoEl.msRequestFullscreen) {
            videoEl.msRequestFullscreen();
        }
      }
    } 
    else if (videoState === 'pause') {
      videoEl.pause();
    }
    else {
      if (isFullScreen()) {
        document.exitFullscreen();
      }
      videoEl.load();
    }
  }, [videoState]);

  useUpdateEffect(() => {
    if (videoState === 'play') {
      if (playTrackingOverride) {
        fireGTMTrackingEvent(playTrackingOverride)
      } else {
        fireGTMTrackingEvent({
          name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
          componentName: gtmComponentName,
          componentGroup: gtmComponentGroup,
          action: CORE_GTM_TRACKING_VALUES.action.playVideo,
          label: src
        });
      }
    }
  }, [videoState]);

  const isFullScreen = () => document.fullscreenElement === videoRef.current;

  function onEnded() {
    setVideoState(false);
    onEnd(isFullScreen());
  };

  function onPaused() {
    setVideoState('pause');
    onPause(isFullScreen());
  };

  function onPlaying() {
    setVideoState('play');
    onPlay(isFullScreen());
  };

  return (
    <div className={block}>
      {
        videoState !== 'play' && 
          <button
          className={`${block}__toggle`}
          onClick={() => setVideoState('play')}
          aria-label={buttonLabel}
          >
            <img
            className={`${block}__image`}
            src={`${assetRootPath}${playIcon}`}
            alt='play'
            />
            {
              playIconHover &&
                <img
                className={`${block}__image-hover`}
                src={`${assetRootPath}${playIconHover}`}
                alt='playHovered'
                />
            }
          </button>
      }
      <video
      className={`${block}__video`}
      ref={videoRef}
      aria-label={label}
      controls={videoState}
      onPlay={onPlaying}
      onEnded={onEnded}
      onPause={onPaused}
      poster={poster}
      playsInline={true}
      loop={false}
      muted={true}
      height='100%'
      width='100%'
      >
        <source src={src} type='video/mp4' />
          {
            captions && 
              <track
              label='English'
              kind='captions'
              srclang='en'
              src={subtitles}
              default
              />
          }
          {
            subtitles &&
              <track
              label='English'
              kind='subtitles'
              srclang='en'
              src={subtitles}
              default
              />
          }
      </video>
    </div>
  );
};

VideoPlayer.propTypes = {
  src: propTypes.string.isRequired,
  poster: propTypes.string,
  captions: propTypes.string,
  playIcon: propTypes.string,
  playIconHover: propTypes.string,
  subtitles: propTypes.string,
  fullscreen: propTypes.bool,
  assetRootPath: propTypes.string,
  onPlay:propTypes.func, 
  onEnd:propTypes.func, 
  onPause:propTypes.func,
  gtmComponentName: propTypes.string,
  gtmComponentGroup: propTypes.string,
  playTrackingOverride: propTypes.object,
  modelName: propTypes.string
};

VideoPlayer.defaultProps = {
  playIcon: 'play-button.svg',
  playIconHover: 'play-button-darker.svg',
  assetRootPath: '/resources/images/',
  onPlay: () => {}, 
  onEnd: () => {}, 
  onPause: () => {},
  gtmComponentName: CORE_GTM_TRACKING_VALUES.componentName.videoPlayer,
  gtmComponentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.campaigns
};